import SDK from "../../Components/SDK";

/**
 * The search API returns names and message bodies AES-encrypted, unlike the SDK calls
 * which hand back plaintext. `SDK.decryptMsg(value, key)` reverses it, where the key is
 * the id the value hangs off: the message id for a body, the user id for a name.
 *
 * decryptMsg returns the string "{}" when it cannot decrypt, so that is treated as a
 * failure and the next candidate key is tried.
 */

const FAILED = "{}";

/**
 * Detects an undecrypted AES value.
 *
 * Base64 alone is not enough — a long single-word name like "Muhammadulhassan" is valid
 * base64 too. What separates real ciphertext is its block structure: AES-CBC output is
 * always a whole number of 16-byte blocks, so the decoded length must be a multiple of 16.
 * That rules out ordinary names, whose decoded lengths are effectively arbitrary.
 */
export const looksEncrypted = (value) => {
    if (typeof value !== "string" || value.length < 16) return false;
    if (value.length % 4 !== 0) return false;
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(value)) return false;

    const padding = (value.match(/=*$/)?.[0] || "").length;
    const decodedBytes = (value.length / 4) * 3 - padding;
    return decodedBytes > 0 && decodedBytes % 16 === 0;
};

const decryptOnce = (value, key) => {
    if (!value || !key || typeof SDK.decryptMsg !== "function") return null;
    try {
        const decrypted = SDK.decryptMsg(value, key);
        if (!decrypted || decrypted === FAILED) return null;
        return decrypted;
    } catch (error) {
        return null;
    }
};

/**
 * Display names from this API are encrypted with a key that is not the user id and is
 * not currently known, so decryption is best-effort only. Callers must prefer the local
 * roster/group data (see resolveDisplayName) — that is how the rest of the app resolves
 * names, and it is what makes the Chats and Groups tabs render correctly.
 *
 * Returns an empty string rather than the ciphertext, so a caller that falls through to
 * the roster never renders base64 at the user.
 */
export const decryptName = (value, userId) => {
    if (!looksEncrypted(value)) return value || "";
    const decrypted = decryptOnce(value, userId);
    return decrypted === null ? "" : decrypted;
};

/**
 * Decrypts a message body, keyed by its message id. The plaintext is the JSON msgBody
 * (e.g. {"message":"Test 5","message_type":"text","nickName":"Pandi"}), so it also
 * yields the sender's name — the one plaintext name source this API gives us.
 *
 * @returns {{ text: string, nickName: string }}
 */
export const decryptMessageBody = (value, messageId) => {
    if (!value) return { text: "", nickName: "", mentionedUsersIds: [] };
    if (!looksEncrypted(value)) return { text: value, nickName: "", mentionedUsersIds: [] };

    // Never fall back to the raw value here — that renders base64 at the user.
    const decrypted = decryptOnce(value, messageId);
    if (!decrypted || typeof decrypted !== "string") return { text: "", nickName: "", mentionedUsersIds: [] };

    if (!decrypted.startsWith("{") && !decrypted.startsWith("[")) {
        return { text: decrypted, nickName: "", mentionedUsersIds: [] };
    }

    try {
        const parsed = JSON.parse(decrypted);
        if (!parsed || typeof parsed !== "object") return { text: decrypted, nickName: "", mentionedUsersIds: [] };
        return {
            text:
                parsed.message ||
                parsed.media?.caption ||
                parsed.media?.fileName ||
                parsed.contact?.name ||
                "",
            nickName: parsed.nickName || "",
            // Mentions are stored as "@[?]" placeholders; these ids fill them in, in order.
            mentionedUsersIds: Array.isArray(parsed.mentionedUsersIds) ? parsed.mentionedUsersIds : []
        };
    } catch (error) {
        return { text: decrypted, nickName: "", mentionedUsersIds: [] };
    }
};
